import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, EXPIRED_FLAG } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock } from "lucide-react";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [expired] = useState(() => sessionStorage.getItem(EXPIRED_FLAG) === "1");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`${t("auth.welcome")}, ${u.name}`);
      nav("/dashboard");
    } catch (err) {
      if (err?.response?.status === 429) {
        const min = Math.ceil((parseInt(err.response.headers?.["retry-after"], 10) || 900) / 60);
        toast.error(t("auth.locked", min));
      } else toast.error(err?.response?.status === 401 ? t("auth.bad_credentials") : formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <div>
            <Logo className="h-8" invert testid="login-side-logo" />
            <div className="mt-3 label-tracked text-slate-500">{t("nav.brand")}</div>
            <div className="mt-1 font-display text-2xl font-bold">{t("nav.tagline")}</div>
          </div>
          <div>
            <div className="font-display text-5xl font-black tracking-tighter leading-[0.95] text-white">
              {t("auth.side_h_a")}<br />{t("auth.side_h_b")}<br />{t("auth.side_h_c")}
            </div>
            <div className="mt-6 max-w-md text-sm text-slate-500 leading-relaxed">
              {t("auth.side_sub")}
            </div>
          </div>
          <div className="font-mono text-xs text-slate-600">© 2026 · v1.0</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <Logo className="h-7" testid="login-mobile-logo" />
            <LanguageSwitcher />
          </div>
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="label-tracked">{t("auth.login_title")}</div>
            <LanguageSwitcher />
          </div>
          <h1 className="font-display text-3xl font-bold mb-8 text-slate-900">{t("auth.login_h")}</h1>
          {expired && (
            <div role="alert" className="mb-6 flex items-start gap-2 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="session-expired-banner">
              <Clock size={16} className="shrink-0 mt-0.5" /> {t("auth.session_expired")}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="label-tracked">{t("auth.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="rounded-sm mt-2 h-11" required />
            </div>
            <div>
              <Label htmlFor="password" className="label-tracked">{t("auth.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="rounded-sm mt-2 h-11" required />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full h-11 rounded-sm bg-slate-900 hover:bg-slate-800">
              {busy ? t("auth.logging_in") : t("auth.login_submit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
